import { PageToolbar, SegmentedTabs, TabPills } from '@skupreme/shared/ui';
import { useSidebar } from '@contexts/sidebar';
import { PermissionPageKey, Permissions } from '@utility/constants/permission';
import { type ReactNode, useMemo } from 'react';
import { useLocation } from 'react-router';
import {
  buildCatalogSections,
  type CatalogMenuEntry,
  isOnPage,
} from '../../lib/sections';
import { useCatalogSource } from '../../hooks/use-catalog-source';

type SidebarContextValue = {
  menus?: { key: string; menus?: CatalogMenuEntry[] }[];
};

/*
 * Moves between the catalog's pages: its sections on top, and a section's own
 * pages under them when it has more than one. The tabs are links, so the
 * router does the switching; selection only mirrors the URL.
 */
export type CatalogSectionTabsProps = {
  /** Pill counts by section id; one given here replaces the sum of the section's pages. */
  counts?: Partial<Record<string, number>>;
  /** The page's own search, filters and actions, on the tabs' row. */
  children?: ReactNode;
};

export function CatalogSectionTabs({
  counts,
  children,
}: CatalogSectionTabsProps) {
  const { pathname } = useLocation();
  const { menus } = (useSidebar() ?? {}) as SidebarContextValue;
  const isV2 = useCatalogSource() === 'v2';

  const sections = useMemo(() => {
    const catalog = menus?.find(menu => menu.key === PermissionPageKey.Catalog);

    return buildCatalogSections(
      catalog?.menus ?? [],
      pathname,
      Permissions.hidden,
      isV2,
    );
  }, [menus, pathname, isV2]);

  const current = sections.find(section =>
    section.pages.some(page => isOnPage(pathname, page.href)),
  );
  const currentPage = current?.pages.find(page =>
    isOnPage(pathname, page.href),
  );

  if (!sections.length) {
    return null;
  }

  return (
    <div>
      <PageToolbar
        leading={
          <TabPills
            label="Catalog"
            selectedKey={current?.id ?? ''}
            items={sections.map(section => ({
              id: section.id,
              label: section.label,
              href: `${section.pages[0].href}/`,
              count:
                counts?.[section.id] ??
                (section.pages.reduce(
                  (sum, page) => sum + (page.count ?? 0),
                  0,
                ) ||
                  undefined),
            }))}
          />
        }
      >
        {children}
      </PageToolbar>
      {current && current.pages.length > 1 && (
        <div className="sk:px-3 sk:pb-2">
          <SegmentedTabs
            label={current.label}
            selectedKey={currentPage?.href ?? ''}
            items={current.pages.map(page => ({
              id: page.href,
              label: page.count ? `${page.label} (${page.count})` : page.label,
              href: `${page.href}/`,
            }))}
          />
        </div>
      )}
    </div>
  );
}
